import { useState, useEffect, useCallback } from 'react';
import type { Match, LiveMatchState, Player, BallColour, FrameResult } from '@shootout-comps/types';
import type { ConnectionStatus } from '../socket/createSocket';
import { getLegalBalls, computeBreak, computeHighBreak, computeAvgShotTime } from './scoring';

export interface UseSpectateMatchOptions {
  matchId: string;
  tournamentId: string;
  apiBaseUrl: string;
  getAccessToken: () => Promise<string | null>;
  // Read-only room join (match:watch/match:unwatch) — deliberately NOT useMatchSocket, which
  // is reserved for genuine match-play and doubles as scorer-role claiming server-side. A
  // spectator/bracket-card view must never be able to bump the real scorer into spectator
  // mode just by viewing the match first.
  useWatchMatchSocket: (matchId: string | null, handlers: Record<string, (data: unknown) => void>) => void;
  useConnectionStatus: () => ConnectionStatus;
  // Overrides which REST path fetches the match — defaults to the public-spectate path.
  // Callers viewing a match they own but that isn't necessarily public (e.g. the bracket
  // page's live match cards) pass the authenticated /matches/:matchId path instead.
  matchFetchPath?: (tournamentId: string, matchId: string) => string;
}

// Read-only counterpart to useMatchPlay for spectating a public tournament's match — same
// data shape (match, ls, legalBalls, breaks, clocks) but deliberately has NO write calls at
// all (no start/shot/undo/pause-clock/resume-clock). useMatchPlay fires several of those as
// side effects (e.g. auto-resume-clock on mount, auto-pause-clock on unmount) which a
// spectator must never trigger, and isn't authorized to anyway — hence a separate, much
// smaller hook rather than a "read-only mode" flag bolted onto useMatchPlay.
const defaultMatchFetchPath = (tournamentId: string, matchId: string) =>
  `/public-tournaments/${tournamentId}/matches/${matchId}`;

export function useSpectateMatch(opts: UseSpectateMatchOptions) {
  const { matchId, tournamentId, apiBaseUrl, getAccessToken, useWatchMatchSocket, useConnectionStatus, matchFetchPath } = opts;
  const API = `${apiBaseUrl}/api`;
  const buildPath = matchFetchPath ?? defaultMatchFetchPath;

  const [match, setMatch] = useState<Match | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [shotElapsed, setShotElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatch = useCallback(async () => {
    const token = await getAccessToken();
    const res = await fetch(`${API}${buildPath(tournamentId, matchId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = await res.json() as Match & { error?: string };
    if (!res.ok) throw new Error(data.error ?? 'Failed to load match');
    return data;
  }, [API, buildPath, tournamentId, matchId, getAccessToken]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetchMatch()
      .then((data) => {
        if (cancelled) return;
        setMatch(data);
        if (data.liveState?.elapsedSeconds) setElapsed(data.liveState.elapsedSeconds);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchMatch]);

  // ── Reconnect resync: any events missed while disconnected are recovered by a plain
  // refetch — every socket payload here is the full Match object, so there's nothing to
  // diff, a fresh fetch is fully sufficient to catch up.
  const { reconnectCount } = useConnectionStatus();
  useEffect(() => {
    if (reconnectCount <= 0) return;
    fetchMatch()
      .then((data) => { setMatch(data); if (data.liveState?.elapsedSeconds) setElapsed(data.liveState.elapsedSeconds); })
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectCount]);

  // ── Sockets — same broadcast events useMatchPlay listens to, purely to update state ──
  useWatchMatchSocket(matchId, {
    'match:tick': (d: unknown) => setElapsed((d as { elapsedSeconds: number }).elapsedSeconds),
    'match:shot': (d: unknown) => setMatch(d as Match),
    'match:started': (d: unknown) => setMatch(d as Match),
    'match:break-off': (d: unknown) => setMatch(d as Match),
    'match:ended': (d: unknown) => setMatch(d as Match),
    'match:clock-paused': (d: unknown) => setMatch(d as Match),
    'match:clock-resumed': (d: unknown) => setMatch(d as Match),
    'match:frame-complete': (d: unknown) => { setMatch(d as Match); setElapsed(0); setShotElapsed(0); },
  });

  // ── Local shot-clock ticker — purely cosmetic countdown, derived from shotStartedAt.
  // No writes, so nothing bad happens if this drifts a little from the server's own timer.
  useEffect(() => {
    const ls = match?.liveState as LiveMatchState | undefined;
    if (!ls || ls.phase !== 'in_progress' || ls.clockPaused || !ls.shotStartedAt) return;
    const start = new Date(ls.shotStartedAt).getTime();
    const tick = () => setShotElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [match?.liveState]);

  // ── Derived — same formulas useMatchPlay uses, so the numbers match exactly ──
  const ls = match?.liveState as LiveMatchState | undefined;
  const p1 = match?.player1 as Player | undefined;
  const p2 = match?.player2 as Player | undefined;
  const notStarted = !ls || ls.phase === 'not_started';
  const inProgress = ls?.phase === 'in_progress';
  const breakOffPending = !!inProgress && (ls as unknown as { breakOffPending?: boolean })?.breakOffPending === true;
  const complete = ls?.phase === 'complete' || match?.status === 'complete';
  const winner = (match?.winner as Player | null) ?? null;
  const curPlayer = ls && p1 && p2 ? (ls.currentPlayerId === ls.player1Id ? p1 : p2) : null;
  const matchDuration = ls?.matchDurationSeconds ?? 0;
  const remaining = matchDuration - elapsed;
  const halfTime = ls?.halfTimeSeconds ?? 0;
  const inSecondHalf = halfTime > 0 && elapsed >= halfTime;
  const shotLimit = ls ? (inSecondHalf ? (ls.shotTimeSecondHalf ?? 0) : (ls.shotTimeFirstHalf ?? 0)) : 0;
  const shotLeft = Math.max(0, shotLimit - shotElapsed);
  const legalBalls = ls ? getLegalBalls(ls.shots, ls.reds_remaining) : new Set<BallColour>();
  const p1Break = ls && inProgress && p1 ? computeBreak(ls.shots, ls.player1Id) : 0;
  const p2Break = ls && inProgress && p2 ? computeBreak(ls.shots, ls.player2Id) : 0;
  const p1HighBreak = ls && p1 ? computeHighBreak(ls.shots, ls.player1Id) : 0;
  const p2HighBreak = ls && p2 ? computeHighBreak(ls.shots, ls.player2Id) : 0;
  const p1AvgShotTime = ls && p1 ? computeAvgShotTime(ls.shots, ls.player1Id) : 0;
  const p2AvgShotTime = ls && p2 ? computeAvgShotTime(ls.shots, ls.player2Id) : 0;
  // Mirrors useMatchPlay's naming exactly so SpectatorView (built for useMatchPlay's output)
  // accepts this hook's result without any adapting at the call site.
  const clockPaused = ls?.clockPaused ?? false;
  const shotWarn = shotLeft <= 5 && shotLeft > 0;
  const shotOver = shotLimit > 0 && shotLeft === 0;
  const displayElapsed = elapsed;
  const isStandard = ls?.isStandard ?? false;

  // ── Frames (best-of-N matches — standard/snooker900 only; absent/1 elsewhere) ──────────
  const bestOf           = match?.bestOf ?? 1;
  const isMultiFrame     = bestOf > 1;
  const framesToWin      = Math.ceil(bestOf / 2);
  const frameWins1       = match?.frameWins1 ?? 0;
  const frameWins2       = match?.frameWins2 ?? 0;
  const frames: FrameResult[] = match?.frames ?? [];
  const currentFrame     = ls?.currentFrame ?? 1;
  const p1MatchHighBreak = Math.max(p1HighBreak, ...frames.map(f => f.highBreak1));
  const p2MatchHighBreak = Math.max(p2HighBreak, ...frames.map(f => f.highBreak2));

  return {
    match, loading, error,
    ls, p1, p2, notStarted, inProgress, breakOffPending, complete, winner, curPlayer,
    elapsed, remaining, matchDuration, inSecondHalf, shotLimit, shotLeft,
    legalBalls, p1Break, p2Break, p1HighBreak, p2HighBreak, p1AvgShotTime, p2AvgShotTime, clockPaused, shotWarn, shotOver, displayElapsed, isStandard,
    bestOf, isMultiFrame, framesToWin, frameWins1, frameWins2, frames, currentFrame,
    p1MatchHighBreak, p2MatchHighBreak,
  };
}

export type UseSpectateMatchResult = ReturnType<typeof useSpectateMatch>;
